/** Workspace calls to the CRM server (routes/workspace.routes.js, auth /auth/me). */
import { crmFetch, dataOf, listOf } from '../chat/api';
import { CRM_FRAME_PATH } from '../crm/crmBridge';
import { api } from '../api/client';
import { createFanslyApi } from '../fansly/api';
import { chatRowsOf } from '../fansly/chats/model';
import { chatsQuery } from '../fansly/chats/useChatList';
import type { ActiveChat, ChatStateRow } from './chats';
import type { FeedItem, FeedPage } from './feed';

export interface PinRow extends Partial<ChatStateRow> {
  account_id: string;
  fan_id: string;
  fan_name: string | null;
  fan_username: string | null;
  fan_avatar: string | null;
  position?: number;
}

export interface Preferences {
  layout: Record<string, unknown> | null;
  settings: Record<string, unknown> | null;
}

const local = { accountId: '', local: true } as const;

export async function fetchPreferences(): Promise<Preferences> {
  const d = dataOf<Preferences>(await crmFetch('/v1/workspace/preferences', local));
  return { layout: d?.layout ?? null, settings: d?.settings ?? null };
}

export async function savePreferences(patch: Partial<Preferences>): Promise<void> {
  await crmFetch('/v1/workspace/preferences', { ...local, method: 'PUT', body: patch as Record<string, unknown> });
}

const pinBody = (p: PinRow) => ({ account_id: p.account_id, fan_id: p.fan_id, fan_name: p.fan_name, fan_username: p.fan_username, fan_avatar: p.fan_avatar });

export async function fetchPins(): Promise<PinRow[]> {
  return (dataOf<{ pins?: PinRow[] }>(await crmFetch('/v1/workspace/pins', local))?.pins ?? []) as PinRow[];
}

/** Saves the board and returns it as the server now has it (with chat state). */
export async function savePins(pins: PinRow[]): Promise<PinRow[]> {
  const body = await crmFetch('/v1/workspace/pins', {
    ...local,
    method: 'PUT',
    body: { pins: pins.map(pinBody) },
  });
  return (dataOf<{ pins?: PinRow[] }>(body)?.pins ?? []) as PinRow[];
}

/** Appends chats to the person's pins (already-pinned ones are skipped); `full` when the 100-pin board left some out. */
export async function addPins(pins: PinRow[]): Promise<{ pins: PinRow[]; full: boolean }> {
  const body = await crmFetch('/v1/workspace/pins/add', { ...local, body: { pins: pins.map(pinBody) } });
  const d = dataOf<{ pins?: PinRow[]; full?: boolean }>(body);
  return { pins: (d?.pins ?? []) as PinRow[], full: d?.full === true };
}

/** Unpins one chat; the person's pins as the server now has them. */
export async function removePin(accountId: string, fanId: string): Promise<PinRow[]> {
  const body = await crmFetch('/v1/workspace/pins/remove', { ...local, body: { account_id: accountId, fan_id: fanId } });
  return (dataOf<{ pins?: PinRow[] }>(body)?.pins ?? []) as PinRow[];
}

export async function fetchActiveChats(windowHours: number): Promise<ActiveChat[]> {
  const hours = Math.max(0.05, Math.round(windowHours * 100) / 100);
  return (dataOf<{ chats?: ActiveChat[] }>(await crmFetch(`/v1/workspace/active?window_hours=${hours}`, local))?.chats ?? []) as ActiveChat[];
}

/** The CRM's view of the signed-in person (its username is what operator attribution stores). */
export async function fetchCrmMe(): Promise<{ id: string; username: string } | null> {
  const send = (token: string | null) =>
    fetch(`${CRM_FRAME_PATH}auth/me`, { headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, cache: 'no-store' });
  let res = await send(api.getToken());
  if (res.status === 401) {
    const fresh = await api.refreshAccessToken();
    if (fresh) res = await send(fresh);
  }
  if (!res.ok) return null;
  const body = (await res.json().catch(() => null)) as { id?: string; username?: string } | null;
  return body?.id ? { id: String(body.id), username: String(body.username || '') } : null;
}

export interface SearchHit {
  account_id: string;
  fan_id: string;
  fan_name: string;
  fan_username: string;
  fan_avatar: string | null;
  snippet: string;
  spent: number | null;
}

type Raw = Record<string, unknown>;

/**
 * Live OnlyFans chat search on one Account: ONE gateway call. `skip_users: 'none'`
 * asks OnlyFans to put the fan's user object (name, @username, avatar) on each
 * chat, so no users/list round trip is needed. Only when a row still comes back
 * without a name (OnlyFans ignored the flag) do we fill the gaps with users/list.
 */
export async function searchChats(accountId: string, query: string, signal?: AbortSignal): Promise<SearchHit[]> {
  const body = await crmFetch('/v1/chats/get', {
    accountId,
    body: { limit: 10, offset: 0, order: 'recent', skip_users: 'none', query },
    signal,
  });
  const hits = searchHitsFrom(accountId, listOf(dataOf(body)));
  const missing = hits.filter((h) => !h.fan_name && !h.fan_username).map((h) => h.fan_id);
  if (missing.length) {
    try {
      const users = listOf(dataOf(await crmFetch('/v1/users/list', { accountId, body: { user_ids: missing, param_name: 'm' }, signal })));
      const byId = new Map(users.map((u) => [String(u.id), u]));
      for (const h of hits) {
        const u = byId.get(h.fan_id);
        if (!u) continue;
        h.fan_name = String(u.name || u.displayName || u.username || h.fan_id);
        h.fan_username = h.fan_username || String(u.username || '');
        h.fan_avatar = h.fan_avatar || avatarOf(u);
      }
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') throw err;
      /* names stay as ids */
    }
  }
  return hits.map((h) => ({ ...h, fan_name: h.fan_name || h.fan_username || h.fan_id }));
}

/**
 * Live Fansly chat search on one Account (ADR 0045): the Fansly Chats page's own
 * search, one gateway call; Fansly puts the fan's account on each chat.
 */
export async function searchFanslyChats(accountId: string, query: string, signal?: AbortSignal): Promise<SearchHit[]> {
  const fansly = createFanslyApi(accountId, { getToken: () => api.getToken(), refreshToken: () => api.refreshAccessToken() });
  const rows = chatRowsOf(await fansly.get('/crm/chats', chatsQuery('recent', query, 0, 10), signal));
  return rows.map((r) => ({
    account_id: accountId,
    fan_id: r.fanId,
    fan_name: r.name || r.username || r.fanId,
    fan_username: r.username,
    fan_avatar: r.avatar || null,
    snippet: r.preview.text,
    spent: null,
  }));
}

/** Search rows from OnlyFans chats (withUser carries the fan when users are not skipped). */
export function searchHitsFrom(accountId: string, rows: Raw[]): SearchHit[] {
  return rows
    .map((row) => {
      const withUser = (row.withUser && typeof row.withUser === 'object' ? row.withUser : {}) as Raw;
      const fanId = String(withUser.id ?? row.withUserId ?? row.fan_id ?? row.id ?? '');
      const last = (row.lastMessage || row.last_message || {}) as Raw;
      const spent = Number(withUser.totalSpent ?? row.totalSpent ?? (withUser.subscribedByData as Raw | undefined)?.totalSumm);
      const name = String(withUser.name || withUser.displayName || row.name || '');
      return {
        account_id: accountId,
        fan_id: fanId,
        // A bare id is not a name (the CRM's own fallback puts the id there).
        fan_name: name && name !== fanId ? name : '',
        fan_username: String(withUser.username || row.username || ''),
        fan_avatar: avatarOf(withUser),
        snippet: String(last.text || last.message || '').replace(/<[^>]+>/g, '').trim(),
        spent: Number.isFinite(spent) ? spent : null,
      };
    })
    .filter((h) => h.fan_id);
}

/** An OnlyFans user's avatar, smallest good thumb first. */
export function avatarOf(u: Raw): string | null {
  const thumbs = (u.avatarThumbs && typeof u.avatarThumbs === 'object' ? u.avatarThumbs : {}) as Raw;
  return String(thumbs.c144 || thumbs.c50 || u.avatar || '') || null;
}

export interface FanInfo {
  name: string;
  username: string;
  avatar: string | null;
}

/** Name and avatar of fans on one Account (gateway users/list), for chats the Workspace knows only partly. */
export async function fetchFanInfo(accountId: string, fanIds: string[]): Promise<Map<string, FanInfo>> {
  const out = new Map<string, FanInfo>();
  if (!fanIds.length) return out;
  const users = listOf(dataOf(await crmFetch('/v1/users/list', { accountId, body: { user_ids: fanIds, param_name: 'm' } })));
  for (const u of users) {
    const id = String(u.id ?? '');
    if (id) out.set(id, { name: String(u.name || u.displayName || u.username || ''), username: String(u.username || ''), avatar: avatarOf(u) });
  }
  return out;
}

// ── Feed (R3) ────────────────────────────────────────────────────────────────

/** One page of the person's Feed (routes/workspaceFeed.routes.js). */
export async function fetchFeedPage(body: {
  settings: Record<string, unknown>;
  chat_since: string;
  exclude: string[];
  scope: string[] | 'all';
  /** A shorter page (the first request): the server clamps it to 1–200. */
  limit?: number;
}, signal?: AbortSignal): Promise<FeedPage> {
  const d = dataOf<Partial<FeedPage>>(await crmFetch('/v1/workspace/feed', { ...local, method: 'POST', body, signal }));
  return { items: (d?.items ?? []) as FeedItem[], has_more: !!d?.has_more, lists: d?.lists ?? [] };
}

export interface FeedVerdict {
  account_id: string;
  fan_id: string;
  bp: number | null;
  temp: number | null;
  verdict: 'pass' | 'fail' | 'pending';
}

/** Scores for fans nearing the front of the queue. */
export async function resolveFeed(settings: Record<string, unknown>, items: { account_id: string; fan_id: string }[]): Promise<FeedVerdict[]> {
  const d = dataOf<{ items?: FeedVerdict[] }>(await crmFetch('/v1/workspace/feed/resolve', { ...local, method: 'POST', body: { settings, items } }));
  return d?.items ?? [];
}

/** Skip: hide this fan from the person's Feed for `hours`. */
export async function snoozeFan(accountId: string, fanId: string, hours: number): Promise<void> {
  await crmFetch('/v1/workspace/feed/snooze', { ...local, method: 'POST', body: { account_id: accountId, fan_id: fanId, hours } });
}

/** "Bring skipped fans back". */
export async function clearFeedSnoozes(): Promise<number> {
  const d = dataOf<{ cleared?: number }>(await crmFetch('/v1/workspace/feed/snoozes', { ...local, method: 'DELETE' }));
  return d?.cleared ?? 0;
}

export interface OfList {
  id: string;
  name: string;
  members: number | null;
}

/** The Account's OnlyFans lists with member counts (gateway /v1/lists/get), for the Lists source picker. */
export async function fetchOfLists(accountId: string): Promise<OfList[]> {
  const out: OfList[] = [];
  for (let offset = 0, page = 0; page < 10; page += 1) {
    const rows = listOf(dataOf(await crmFetch('/v1/lists/get', { accountId, body: { limit: 50, offset } })));
    for (const l of rows) {
      const raw = l.usersCount ?? l.users_count ?? l.totalUsers ?? l.total_users ?? l.membersCount ?? l.count;
      const n = Number(raw);
      out.push({ id: String(l.id), name: String(l.name || l.listName || l.id), members: raw == null || !Number.isFinite(n) ? null : n });
    }
    if (rows.length < 50) break;
    offset += rows.length;
  }
  return out;
}
